import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

import {
    ListRenderItemInfo,
    StyleProp,
    TextStyle,
    View,
    ViewStyle,
} from 'react-native';

import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { ButtonArrowDown } from 'assets/images';
import IconArrowDownSmall from 'assets/images/svgs/ico_arrow_down_small.svg';

import { LANGUAGE_KEYS } from 'config/languageKeys';
import { BUTTON_RENT, BUTTON_SUBSCRIBE } from 'config/testID';

import { selectScreenReaderEnabled } from 'store/config/selectors';
import { ProductType, PurchaseProduct } from 'store/railsBuilder/reducer.types';

import { isPhone, isTabletOrWeb, isWeb } from 'utils/responsiveness';

import styles from 'screens/Detail/components/DetailPageDropdownButton/styles';
import { ManageRecordingItem } from 'screens/Detail/types';

import BottomSheetAccessibleList from 'components/BottomSheetAccessibleList';
import BottomSheetList from 'components/BottomSheetList';
import BottomSheetRowItem from 'components/BottomSheetRowItem';
import rowStyles from 'components/BottomSheetRowItem/styles';
import ActionButton from 'components/CustomButtons/ActionButton';
import PickerList from 'components/PickerList';

type DropdownCategory = {
    key: string;
    label: string;
    isSelected: boolean;
    testID: string;
    product: PurchaseProduct | ManageRecordingItem;
};

export type ModalPositionType = {
    top?: number;
    left?: number;
    bottom?: number;
    right?: number;
    width?: number;
};

type Props = {
    onSelectItem: (item: PurchaseProduct | ManageRecordingItem) => void;
    isRecordingDropdownVisible?: boolean;
    showRecordingDropdown?: () => void;
    hideRecordingDropdown?: () => void;
    title?: string;
    rentOptions?: PurchaseProduct[] | undefined;
    subscriptionOptions?: PurchaseProduct[] | undefined;
    recordingOptions?: ManageRecordingItem[];
    fillColor?: string;
    buttonStyle?: StyleProp<ViewStyle>;
    buttonTextStyle?: TextStyle;
    useWhiteArrowIcon?: boolean;
    canCloseButtonDropdown?: boolean;
};

const {
    DETAILS_DROPDOWN: { SCREEN_DETAIL_MANAGE_RECORDING_BUTTON },
} = LANGUAGE_KEYS;
const languageKeyMap = {
    [ProductType.TRANSACTION]: LANGUAGE_KEYS.DETAILS_DROPDOWN.BUTTON_RENT,
    [ProductType.SUBSCRIPTION]: LANGUAGE_KEYS.DETAILS_DROPDOWN.BUTTON_SUBSCRIBE,
};

function DetailPageDropdownButton({
    title = LANGUAGE_KEYS.DETAILS_DROPDOWN.BUTTON_RENT_SUBSCRIBE,
    onSelectItem,
    isRecordingDropdownVisible = false,
    showRecordingDropdown,
    hideRecordingDropdown,
    rentOptions,
    subscriptionOptions,
    recordingOptions,
    fillColor,
    buttonStyle,
    buttonTextStyle,
    useWhiteArrowIcon,
    canCloseButtonDropdown,
}: Props) {
    const { t } = useTranslation();

    const screenReaderEnabled = useSelector(selectScreenReaderEnabled);

    const [modalStartingPosition, setModalStartingPosition] = useState<
        ModalPositionType | undefined
    >(undefined);
    const [modalWidth, setModalWidth] = useState<number>(0);
    const [isDropdownVisible, setIsDropdownVisible] = useState(false);

    const options: DropdownCategory[] = useMemo(() => {
        if (rentOptions && subscriptionOptions) {
            return [...rentOptions, ...subscriptionOptions].map((option) => ({
                key: `${languageKeyMap[option.type]}_${option.id}`,
                label: t(languageKeyMap[option.type]),
                testID:
                    option.type === ProductType.TRANSACTION
                        ? BUTTON_RENT
                        : BUTTON_SUBSCRIBE,
                product: option,
                isSelected: false,
            }));
        }

        if (recordingOptions) {
            return recordingOptions.map((option) => ({
                key: option.key,
                label: t(option.label),
                testID: option.testID,
                product: option,
                isSelected: false,
            }));
        }

        return [];
    }, [rentOptions, subscriptionOptions, recordingOptions]);

    const Icon = useMemo(() => {
        return (
            <View
                style={{
                    transform: [
                        {
                            rotate: `${
                                isDropdownVisible || isRecordingDropdownVisible
                                    ? 180
                                    : 0
                            }deg`,
                        },
                    ],
                }}
            >
                {useWhiteArrowIcon ? (
                    <IconArrowDownSmall />
                ) : (
                    <ButtonArrowDown />
                )}
            </View>
        );
    }, [isDropdownVisible, isRecordingDropdownVisible, useWhiteArrowIcon]);

    const modalWidthFallback = isWeb ? 228 : 218;
    const itemRowHeight = isWeb ? 54 : 60;

    const bottomSheetRef = useRef<any | null>(null);

    const onPress = useCallback(() => {
        if (showRecordingDropdown) {
            setTimeout(() => {
                showRecordingDropdown();
            }, 50);
        } else {
            setIsDropdownVisible(true);
        }

        bottomSheetRef.current?.present();
    }, [showRecordingDropdown]);

    const onClose = useCallback(() => {
        if (hideRecordingDropdown) {
            hideRecordingDropdown();
        } else {
            setIsDropdownVisible(false);
        }

        bottomSheetRef.current?.close();
    }, [hideRecordingDropdown]);

    const renderBottomSheetRow = useCallback(
        ({ item }: ListRenderItemInfo<DropdownCategory>) => (
            <BottomSheetRowItem
                item={item}
                onPress={(_item) => onSelectItem(_item?.product)}
                testID={item.testID}
            />
        ),
        [onSelectItem],
    );

    useEffect(() => {
        if (canCloseButtonDropdown) {
            onClose();
        }
    }, [canCloseButtonDropdown, onClose]);

    const memoizedModalStartingPosition = useMemo(
        () => ({
            ...modalStartingPosition,
            top: isWeb
                ? (modalStartingPosition?.top ?? 0) -
                  (options.length * itemRowHeight + itemRowHeight / 2)
                : (modalStartingPosition?.top ?? 0) -
                  (options.length * itemRowHeight - 1.9 * itemRowHeight),
        }),
        [modalStartingPosition, options.length],
    );

    if (!options.length) {
        return null;
    }

    return (
        <>
            <ActionButton
                title={title}
                onPress={onPress}
                fillColor={fillColor}
                buttonStyle={[styles.button, buttonStyle]}
                buttonTextStyle={[styles.buttonText, buttonTextStyle]}
                numberOfLines={1}
                IconAfter={Icon}
                setModalPosition={setModalStartingPosition}
                setModalWidth={setModalWidth}
                testID={title}
            />
            {isTabletOrWeb && (
                <PickerList
                    data={options}
                    isModalVisible={
                        isRecordingDropdownVisible || isDropdownVisible
                    }
                    modalStartingPosition={memoizedModalStartingPosition}
                    listHeight={options.length * itemRowHeight}
                    listWidth={
                        modalWidth !== 0 ? modalWidth : modalWidthFallback
                    }
                    rowHeight={itemRowHeight}
                    renderItem={renderBottomSheetRow}
                    keyExtractor={keyExtractor}
                    onClose={onClose}
                />
            )}
            {isPhone && screenReaderEnabled && (
                <BottomSheetAccessibleList
                    visible={isRecordingDropdownVisible}
                    data={options}
                    renderRowItem={renderBottomSheetRow}
                    keyExtractor={keyExtractor}
                    headerTitle={t(SCREEN_DETAIL_MANAGE_RECORDING_BUTTON)}
                    onClose={onClose}
                />
            )}
            {isPhone && !screenReaderEnabled && (
                <BottomSheetList
                    ref={bottomSheetRef}
                    data={options}
                    renderRowItem={renderBottomSheetRow}
                    keyExtractor={keyExtractor}
                    rowHeight={rowStyles.container.height}
                    headerTitle={t(SCREEN_DETAIL_MANAGE_RECORDING_BUTTON)}
                    onClose={onClose}
                    onDismiss={onClose}
                />
            )}
        </>
    );
}

function keyExtractor(item: DropdownCategory) {
    return item.key as string;
}

export default DetailPageDropdownButton;
